import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import ToastContainer from "../../../Utils/ToastContainer";
import {
  BookOpenCheck,
  SlidersHorizontal,
  X,
  LayoutGrid,
  Table2,
  Pencil,
  Search,
} from "lucide-react";
import useCatalogue from "../../Services/Catalogue/useCatalogue";
import ClientSearchSelect from "../../common/ClientSearchSelect";
// New: Master Category modal (kept in separate file)
import AddMasterCategoryModal from "../Catalogue/AddMasterCategoryModal";
import AddPrinterModal from "../AddPrinterModal";
import CatalogueTableView from "../Catalogue/CatalogueTableView";
import AdminUploadItems from "../AdminUploadItems";
import CatalogueView from "../Catalogue/CatalogueView";
import CatalogueItemModal from "../Catalogue/CatalogueItemModal";

// Predefined customization sets (kept from your old file)
const customizationSets = [
  {
    customize_field: "Sweetness",
    options: ["Less Sweet", "Sweet", "High Sweet"],
  },
  { customize_field: "Spice Level", options: ["Mild", "Medium", "Spicy"] },
  {
    customize_field: "Oil Preference",
    options: ["Low Oil", "Normal Oil", "Extra Oil"],
  },
  {
    customize_field: "Butter Topping",
    options: ["No Butter", "Butter", "Extra Butter"],
  },
  { customize_field: "Cooking Type", options: ["Fried", "Grilled", "Steamed"] },
  { customize_field: "Onion & Garlic", options: ["No Onion", "No Garlic"] },
];

// Same category options you were using earlier (you can tweak if needed)
const VARIANT_CATEGORY_GROUPS = [
  {
    label: "Industry",
    options: [
      "Food",
      "Bakery",
      "Grocery",
      "Dairy",
      "Beverages",
      "Household",
      "Personal Care",
      "Pharmacy",
      "Pet Care",
      "Baby Care",
      "Stationery",
      "Home & Kitchen",
      "Cleaning",
      "Electronics",
      "Fashion",
      "Health & Wellness",
    ],
  },
  {
    label: "Food Types",
    options: [
      "Fruits & Vegetables",
      "Staples & Grains",
      "Rice & Flour",
      "Pulses & Lentils",
      "Oils & Ghee",
      "Masala & Spices",
      "Snacks & Namkeen",
      "Biscuits & Cookies",
      "Chocolates & Sweets",
      "Breakfast & Cereals",
      "Instant & Ready-to-Eat",
      "Bakery & Breads",
      "Cakes & Pastries",
      "Dairy & Cheese",
      "Paneer & Tofu",
      "Eggs & Meat",
      "Seafood",
      "Frozen Foods",
      "Beverages - Soft Drinks",
      "Beverages - Juices",
      "Tea & Coffee",
      "Water & Ice",
    ],
  },
];

const MAIN_TABS = [
  {
    key: "catalogue",
    label: "Catalogue View",
    icon: LayoutGrid,
  },
  {
    key: "table",
    label: "Table View",
    icon: Table2,
  },
  {
    key: "UploadItems",
    label: "Upload Items",
    icon: Table2,
  },
];

export default function AdminCatalogue() {
  const { api, AllClients, selectedClient, setSelectedClient } =
    useContext(GlobalContext);

  const {
    updateCatalogueSizeId,
    getCatalogueItemImages,
    updateCatalogueItemImage,
    uploadAdminItemImage,
    getAllInventoryVariant,
    enableDisablePackagingCharges,
    updateItemAvailabiltyStatus,
    getCatalogueItemsMaster,
    getPackagingChargesAndTriggers,
    getRawInventoryMaster,
    insertCatalogItemVariant,
    updateCatalogItems,
    insertRawInventoryMaster,
    updateCatalogItemsVisibility,
    getInventorySubCategories,
    getRackDetails,
    changeItemAvailability,
    updateCategoryCustomization,
    getPrinters,
    savePrinterToItem: savePrinterToItemApi,
  } = useCatalogue();

  //buttons
  const [itemVisibilityMap, setItemVisibilityMap] = useState({});
  const [rawItemName, setRawItemName] = useState("");
  const [addingRawItem, setAddingRawItem] = useState(false);
  const [variantStatusMap, setVariantStatusMap] = useState({});
  const [mainTab, setMainTab] = useState("catalogue");
  const [catalogueTab, setCatalogueTab] = useState("catalogue");
  const [subCategories, setSubCategories] = useState([]);
  const [racks, setRacks] = useState([]);
  const [rawInventoryList, setRawInventoryList] = useState([]);
  const [loadingRaw, setLoadingRaw] = useState(false);
const [catalogueImageSearch, setCatalogueImageSearch] = useState("");

  const [variantForm, setVariantForm] = useState({
    variation: "",
    price: "",
  });
  const [selectedItemForVariant, setSelectedItemForVariant] = useState(null);
  const [isItemPackagingEnabled, setIsItemPackagingEnabled] = useState(false);
  const [packagingBusyId, setPackagingBusyId] = useState(null);

  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  // ---- client selection ----
  const userID = selectedClient?.code || "";
  const userPKID = selectedClient?.pkid || "";

  // ---- catalogue state ----
  const [loading, setLoading] = useState(false);
  const [catalogueStore, setCatalogueStore] = useState([]); // [{cat_id, category_name, menu_items:[]}, ...]
  const [searchText, setSearchText] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [availabilityData, setAvailabilityData] = useState({}); // { item_id: {homeDelivery, takeaway, dineIn} }

  // ---- view/edit modal ----
  const [viewModalItem, setViewModalItem] = useState(null);
  const [viewTab, setViewTab] = useState("details");
  const [isEditing, setIsEditing] = useState(false);
  const [editableDetails, setEditableDetails] = useState({});

  // ---- customization modal ----
  const [customizationModal, setCustomizationModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCustomizations, setSelectedCustomizations] = useState([]); // array of customize_field names
  const [preSelectedCustomization, setPreSelectedCustomization] = useState([]);

  // ---- printer modal ----
  const [addPrinterOpen, setAddPrinterOpen] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [printerLoading, setPrinterLoading] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);

  // ---- catalogue image modal ----
  const [catalogueImageOpen, setCatalogueImageOpen] = useState(false);
  const [catalogueImageRows, setCatalogueImageRows] = useState([]);
  const [catalogueImageLoading, setCatalogueImageLoading] = useState(false);
  const [catalogueImageBusyId, setCatalogueImageBusyId] = useState("");

  // ---- confirm dialog ----
  const [confirm, setConfirm] = useState(null);
  const openConfirm = (title, message, onConfirm) =>
    setConfirm({ title, message, onConfirm });

  // ---- master category modal ----
  const [masterCategoryOpen, setMasterCategoryOpen] = useState(false);
  const anchorRef = useRef(null);

  const markAnchor = (catId, itemId) => {
    anchorRef.current = { catId: String(catId), itemId: String(itemId) };
  };

  // ===== Helpers =====
  const handleHideItem = async (item) => {
    const current =
      itemVisibilityMap[item.item_id] ?? item?.prices?.[0]?.visibility === "Y";

    const next = !current;

    // instant UI update
    setItemVisibilityMap((prev) => ({
      ...prev,
      [item.item_id]: next,
    }));

    const result = await updateCatalogItemsVisibility({
      userId: userID,
      itemId: item.item_id,
      status: next ? "Y" : "N",
    });

    if (result.success) {
      toast.success(result.message);
    } else {
      // rollback
      setItemVisibilityMap((prev) => ({
        ...prev,
        [item.item_id]: current,
      }));

      toast.error(result.message);
    }
  };
  const handleVariantStatusToggle = async ({
    variantId,
    type, // "stock" | "visibility"
  }) => {
    if (!variantId) return;

    const currentState = variantStatusMap[variantId] || {
      stock: "N",
      visibility: "N",
    };

    const updatedState = {
      ...currentState,
      [type]: currentState[type] === "Y" ? "N" : "Y",
    };

    // optimistic update
    setVariantStatusMap((prev) => ({
      ...prev,
      [variantId]: updatedState,
    }));

    try {
      const result = await updateItemAvailabiltyStatus({
        id: variantId,
        isStock: updatedState.stock,
        isVisible: updatedState.visibility,
      });

      if (!result.success) {
        // rollback
        setVariantStatusMap((prev) => ({
          ...prev,
          [variantId]: currentState,
        }));

        toast.error(result.message || "Failed to update");
        return;
      }

      toast.success("Status updated");
    } catch (err) {
      console.error(err);

      // rollback
      setVariantStatusMap((prev) => ({
        ...prev,
        [variantId]: currentState,
      }));

      toast.error("Failed to update");
    }
  };

  const filteredCatalogueImageRows = catalogueImageRows.filter((row) => {
    const searchValue = catalogueImageSearch.trim().toLowerCase();

    if (!searchValue) return true;

    const itemName = String(
      row?.item_name || row?.itemName || "",
    ).toLowerCase();

    const pkItemId = String(
      row?.pk_item_id ?? row?.pkItemId ?? row?.item_id ?? "",
    ).toLowerCase();

    return itemName.includes(searchValue) || pkItemId.includes(searchValue);
  });

  const handleEditClick = async () => {
    setIsEditing(true);

    setEditableDetails((prev) => {
      const vm = viewModalItem;
      const idx = prev.selectedPriceIndex ?? 0;
      const row = vm?.prices?.[idx] || vm?.prices?.[0] || {};

      return {
        ...prev,

        item_id: vm?.item_id || prev.item_id || "",
        item_name: vm?.item_name || prev.item_name || "",
        item_description: vm?.item_description || prev.item_description || "",
        photo: vm?.photo || prev.photo || "",

        category: vm?.sub_category || prev.category || "",
        categoryId:
          prev.categoryId !== undefined && prev.categoryId !== ""
            ? String(prev.categoryId)
            : String(vm?.parentCategoryId ?? ""),

        rackNo: vm?.rackNumber ?? prev.rackNo ?? "",
        expiryDate:
          vm?.expiryDate?.replaceAll("/", "-") || prev.expiryDate || "",

        id: row.id || prev.id || "",
        variantId: String(row.variantId ?? row.size_id ?? prev.variantId ?? ""),
        variantName: row.variantName || row.size || prev.variantName || "",

        selectedPriceIndex: idx,

        sizeId: row.size_id || prev.sizeId || "",
        sizeName: row.size || prev.sizeName || "",
        size: row.size || prev.size || "",

        sellingPrice: row.price ?? prev.sellingPrice ?? "",

        homeDeliveryPrice:
          row.home_delivery_price ?? prev.homeDeliveryPrice ?? "",
        homePrice:
          row.home_delivery_price ??
          prev.homePrice ??
          prev.homeDeliveryPrice ??
          "",

        takewayPrice: row.takeaway_price ?? prev.takewayPrice ?? "",
        takeawayPrice:
          row.takeaway_price ?? prev.takeawayPrice ?? prev.takewayPrice ?? "",

        diningPrice: row.dining_price ?? prev.diningPrice ?? "",

        roomOrderPrice: row.room_order_price ?? prev.roomOrderPrice ?? "",
        roomPrice:
          row.room_order_price ?? prev.roomPrice ?? prev.roomOrderPrice ?? "",

        carOrderPrice: row.car_order_price ?? prev.carOrderPrice ?? "",
        carPrice:
          row.car_order_price ?? prev.carPrice ?? prev.carOrderPrice ?? "",

        taxPercentage: row.item_tax ?? prev.taxPercentage ?? "",
        tax: row.item_tax ?? prev.tax ?? prev.taxPercentage ?? "",
      };
    });

    try {
      const subRes = await getInventorySubCategories({
        userid: userID,
        category: "",
      });

      if (subRes.success) {
        setSubCategories(subRes.data || []);
      } else {
        setSubCategories([]);
        toast.error(subRes.message || "Failed to load sub-categories");
      }

      const rackRes = await getRackDetails({
        userid: userID,
      });

      if (rackRes.success) {
        setRacks(rackRes.data || []);
      } else {
        setRacks([]);
        toast.error(rackRes.message || "Failed to load rack details");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading edit data");
    }
  };

  const handleSaveFromViewMode = async () => {
    const d = editableDetails || {};

    // Edit form uses homePrice / takeawayPrice / roomPrice / carPrice / tax; view path uses *Delivery* names.
    const homeDel = d.homeDeliveryPrice ?? d.homePrice ?? "";
    const takeAway = d.takewayPrice ?? d.takeawayPrice ?? "";
    const roomOrd = d.roomOrderPrice ?? d.roomPrice ?? "";
    const carOrd = d.carOrderPrice ?? d.carPrice ?? "";
    const taxPct = d.taxPercentage ?? d.tax ?? "";

    const payload = {
      userid: userID,

      itemId: d.item_id,
      itemName: d.item_name,
      desc: d.item_description,

      sizeId: d.sizeId,
      category: d.category,
      size: d.size || d.sizeName || d.variantName,

      sellingPrice: d.sellingPrice,
      homeDeliveryPrice: homeDel,
      takewayPrice: takeAway,
      diningPrice: d.diningPrice,
      roomOrder: roomOrd,
      carOrder: carOrd,

      taxPercentage: taxPct,

      id: d.id,

      image: d.photo,

      categoryId: d.categoryId,
      rackNo: d.rackNo,

      expiryDate: d.expiryDate?.replaceAll("-", "/"),

      variantName: d.variantName,
      variantId: d.variantId,
    };

    try {
      const res = await updateCatalogItems(payload); // ✅ using service

      if (res.success) {
        toast.success(res.message || "Updated successfully");

        //  refresh catalogue (IMPORTANT)
        await getCatalogueItemsMaster({
          userId: userID,
        });

        allcatalogueAPI(true); // keeps scroll + refresh UI

        setIsEditing(false);
      } else {
        toast.error(res.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  const togglePackagingCharge = async (item) => {
    if (!item?.item_id) return;

    const current = String(item.packaging_charge || "N").toUpperCase();
    const next = current === "Y" ? "N" : "Y";

    setPackagingBusyId(String(item.item_id));

    try {
      const res = await enableDisablePackagingCharges({
        usCode: userID,
        itemId: item.item_id,
        trigger: next,
      });

      if (res.success) {
        Swal.fire("Success", res.message, "success");

        // refresh list
        refreshCataloguePreserveScroll();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch {
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setPackagingBusyId(null);
    }
  };

  // Refresh catalogue but keep current scroll position
  const refreshCataloguePreserveScroll = async () => {
    const currentScrollY = window.scrollY || 0;
    await allcatalogueAPI();
    window.scrollTo({ top: currentScrollY, behavior: "auto" });
  };

  const handleSaveVariant = async (variant) => {
    const result = await updateItemAvailabiltyStatus({
      id: variant.id,
      isVisible: variant.visibility,
      isStock: variant.inStock,
    });

    if (result.success) {
      toast.success(result.message);

      // refresh catalogue
      allcatalogueAPI(true);
    } else {
      toast.error(result.message);
    }
  };

  const handleAddVariant = async () => {
    if (!variantForm.variation || !variantForm.price) {
      toast.error("Please fill all fields");
      return;
    }

    const result = await insertCatalogItemVariant({
      userId: userID,
      itemId: selectedItemForVariant?.item_id,
      price: variantForm.price,
      variation: variantForm.variation,
      variationId: "", // new variant
    });

    if (result.success) {
      toast.success(result.message);

      // reset form
      setVariantForm({ variation: "", price: "" });

      // close modal
      setIsVariantModalOpen(false);

      // refresh catalogue
      allcatalogueAPI(true);
    } else {
      toast.error(result.message);
    }
  };

  const handleVariantStockToggle = async (item, price) => {
    const variantId = String(price.id || price.row_id || price.size_id || "");

    if (!variantId) {
      toast.error("Variant ID missing");
      return;
    }

    const currentStock = price.inStock === "Y";
    const nextStock = currentStock ? "N" : "Y";

    const currentVisible = price.visibility === "Y" ? "Y" : "N";

    // instant UI update
    setVariantStatusMap((prev) => ({
      ...prev,
      [variantId]: nextStock,
    }));

    try {
      const result = await updateItemAvailabiltyStatus({
        id: variantId,
        isVisible: currentVisible,
        isStock: nextStock,
      });

      if (result.success) {
        toast.success(nextStock === "Y" ? "Stock ON" : "Stock OFF");
      } else {
        // rollback
        setVariantStatusMap((prev) => ({
          ...prev,
          [variantId]: currentStock ? "Y" : "N",
        }));

        toast.error(result.message);
      }
    } catch (err) {
      console.error(err);

      setVariantStatusMap((prev) => ({
        ...prev,
        [variantId]: currentStock ? "Y" : "N",
      }));

      toast.error("Failed to update stock");
    }
  };

  const filteredCategories = useMemo(() => {
    if (!catalogueStore) return [];

    return catalogueStore
      .map((category) => {
        const filteredItems = (category.menu_items || []).filter((item) => {
          const matchSearch = (item.item_name || "")
            .toLowerCase()
            .includes(searchText.toLowerCase());

          if (!matchSearch) return false;

          const prices = item.prices || [];

          // OUT OF STOCK TAB
          if (catalogueTab === "outofstock") {
            return prices.some((p) => {
              const variantId = String(p.id || p.row_id || p.size_id || "");

              const effectiveStock =
                variantStatusMap[variantId]?.stock ?? p.inStock;

              return effectiveStock === "N";
            });
          }

          // INVISIBLE STOCK TAB
          if (catalogueTab === "invisibleStock") {
            return prices.some((p) => {
              const variantId = String(p.id || p.row_id || p.size_id || "");

              const effectiveVisible =
                variantStatusMap[variantId]?.visibility ?? p.visibility;

              return effectiveVisible === "N";
            });
          }

          return true;
        });

        return {
          ...category,
          menu_items: filteredItems,
        };
      })
      .filter((cat) => cat.menu_items.length > 0);
  }, [catalogueStore, searchText, catalogueTab, variantStatusMap]);

  useEffect(() => {
    // When searching → expand matching categories
    if (searchText.trim()) {
      const matched = filteredCategories.map((c) => String(c.cat_id));
      setExpandedCategories(matched);
      return;
    }

    // Prevent resetting user-opened categories after refresh
    if (expandedCategories.length > 0) return;

    // Initial first load only
    const first = String(catalogueStore?.[0]?.cat_id || "");

    if (first) {
      setExpandedCategories([first]);
    }
  }, [filteredCategories, searchText, catalogueStore]);

  const fetchVariants = async () => {
    setLoadingVariants(true);
    try {
      const res = await getAllInventoryVariant({ userid: userID });

      if (res.success) {
        setVariants(res.data || []);
      } else {
        setVariants([]);
      }
    } catch {
      setVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  // ===== API Calls =====
  const allcatalogueAPI = async (preserveScroll = false) => {
    const currentScrollY = preserveScroll ? window.scrollY || 0 : null;

    setLoading(true);

    try {
      const result = await getCatalogueItemsMaster({
        userId: userID,
      });

      if (result.success) {
        const catalogue = result.data || [];

        // catalogue store
        setCatalogueStore(catalogue);

        // unified variant stock + visibility state
        const nextVariantState = {};

        catalogue.forEach((cat) => {
          (cat.menu_items || []).forEach((item) => {
            (item.prices || []).forEach((price) => {
              const variantId = String(
                price.id || price.row_id || price.size_id || "",
              );

              nextVariantState[variantId] = {
                stock: price.inStock === "Y" ? "Y" : "N",
                visibility: price.visibility === "Y" ? "Y" : "N",
              };
            });
          });
        });

        setVariantStatusMap(nextVariantState);
      } else {
        setCatalogueStore([]);
      }
    } catch (e) {
      console.error("Catalogue fetch error:", e);
      setCatalogueStore([]);
    } finally {
      setLoading(false);

      if (preserveScroll && currentScrollY !== null) {
        window.scrollTo({
          top: currentScrollY,
          behavior: "auto",
        });
      }
    }
  };

  const pullCatalogue = async () => {
    if (!userID) return;

    setLoading(true);

    try {
      const result = await getCatalogueItemsMaster({
        userId: userID,
      });

      if (result.success) {
        const catalogue = result.data || [];

        setCatalogueStore(catalogue);

        // seed availability map (UI logic stays here)

        const next = {};
        catalogue.forEach((cat) =>
          (cat.menu_items || []).forEach((it) => {
            next[it.item_id] = {
              homeDelivery: it.homeDeliveryAvailability,
              takeaway: it.takeawayAvailability,
              dineIn: it.diningAvailability,
            };
          }),
        );

        setAvailabilityData(next);
      } else {
        setCatalogueStore([]);
        toast.error(result.message);
      }
    } catch (e) {
      setCatalogueStore([]);
      toast.error("Error loading catalogue");
    } finally {
      setLoading(false);
    }
  };

  const getPackagingChargesAndTriggersApi = async () => {
    try {
      const result = await getPackagingChargesAndTriggers({
        userId: userID,
      });

      if (result.success) {
        const row = result.data?.[0];

        // UI logic stays here
        setIsItemPackagingEnabled(row?.packagingItemWise === "Y");
      } else {
        // optional
        toast.error(result.message);
      }
    } catch (err) {
      console.error("Failed to fetch packaging config");
    }
  };

  const submitAvailability = async (itemId) => {
    const updated = availabilityData[itemId];

    openConfirm(
      "Save Availability?",
      "Apply availability changes for this item?",
      async () => {
        const result = await changeItemAvailability({
          userid: userID,
          itemId,
          homeDelivery: updated?.homeDelivery,
          takeaway: updated?.takeaway,
          dineIn: updated?.dineIn,
        });

        if (result.success) {
          toast.success(result.message || "Availability saved");
          pullCatalogue();
        } else {
          toast.error(result.message || "Failed to save availability");
        }
      },
    );
  };

  const applyCustomizationToCategory = async () => {
    const customizationArray = customizationSets
      .filter((set) => selectedCustomizations.includes(set.customize_field))
      .map((set) => ({
        customization_type: "customize",
        customize_field: set.customize_field,
        options: set.options,
      }));

    openConfirm(
      "Apply Customizations?",
      "These options will be applied to all items within this category.",
      async () => {
        const result = await updateCategoryCustomization({
          userid: userID,
          categoryId: selectedCategoryId,
          customizations: customizationArray,
        });

        if (result.success) {
          toast.success(result.message || "Customization updated");
          setCustomizationModal(false);
          pullCatalogue();
        } else {
          toast.error(result.message || "Failed to update customization");
        }
      },
    );
  };

  const fetchPrinters = async () => {
    if (!userID) return;

    setPrinterLoading(true);

    try {
      const result = await getPrinters({
        userid: userID,
      });

      if (result.success) {
        setPrinters(result.data || []);
      } else {
        setPrinters([]);
        toast.error(result.message || "Failed to load printers");
      }
    } catch {
      setPrinters([]);
      toast.error("Error fetching printers");
    } finally {
      setPrinterLoading(false);
    }
  };

  const savePrinterToItem = async () => {
    if (!selectedPrinter || !viewModalItem) {
      return toast.error("Select a printer");
    }

    const result = await savePrinterToItemApi({
      userid: userID,
      itemId: viewModalItem.item_id,
      printerCode: selectedPrinter.printerCode,
      printerAlias: selectedPrinter.printerAlias,
    });

    if (result.success) {
      toast.success(result.message || "Printer saved");
    } else {
      toast.error(result.message || "Failed to save printer");
    }
  };

  const getRawInventory = async () => {
    setLoadingRaw(true);

    try {
      const result = await getRawInventoryMaster();

      if (result.success) {
        setRawInventoryList(result.data);
      } else {
        setRawInventoryList([]);
        toast.error(result.message);
      }
    } catch (err) {
      setRawInventoryList([]);
      toast.error("Error fetching raw inventory");
    } finally {
      setLoadingRaw(false);
    }
  };

  const handleAddRawInventory = async () => {
    if (!rawItemName.trim()) {
      toast.error("Please enter raw item name");
      return;
    }

    setAddingRawItem(true);

    try {
      const result = await insertRawInventoryMaster({
        itemName: rawItemName,
      });

      if (result.success) {
        toast.success(result.message || "Raw inventory added successfully");

        // clear input
        setRawItemName("");

        // instantly refresh updated list
        await getRawInventory();
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add raw inventory");
    } finally {
      setAddingRawItem(false);
    }
  };

  const handleUpdateSizeId = async (usCode) => {
    const data = await updateCatalogueSizeId({ usCode });

    if (data?.responseCode === "00") {
      toast.success("SizeId updated successfully");
    } else {
      toast.error(data?.responseMessage || "Failed to update sizeId");
    }
  };

  // ===== Catalogue Image =====
  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const loadCatalogueImageRows = async () => {
    if (!userID) {
      setCatalogueImageRows([]);
      toast.error("Please select client first");
      return;
    }

    try {
      setCatalogueImageLoading(true);

      const result = await getCatalogueItemImages({
        fk_user_id: userID,
      });

      if (result?.success) {
        setCatalogueImageRows(Array.isArray(result?.data) ? result.data : []);
      } else {
        setCatalogueImageRows([]);
        toast.error(result?.message || "Failed to load catalogue images");
      }
    } catch (error) {
      console.error("loadCatalogueImageRows error:", error);
      setCatalogueImageRows([]);
      toast.error("Failed to load catalogue images");
    } finally {
      setCatalogueImageLoading(false);
    }
  };

  const handleOpenCatalogueImage = async () => {
    if (!userID) {
      toast.error("Please select client first");
      return;
    }

    setCatalogueImageOpen(true);
    await loadCatalogueImageRows();
  };

 const handleCatalogueImageChange = async (event, row) => {
   const file = event.target.files?.[0];

   event.target.value = "";

   if (!file) return;

   if (!file.type?.startsWith("image/")) {
     toast.error("Please select a valid image");
     return;
   }

   if (!userID) {
     toast.error("Please select client first");
     return;
   }

   const pkItemId = String(
     row?.pk_item_id ?? row?.pkItemId ?? row?.item_id ?? "",
   ).trim();

   if (!pkItemId) {
     toast.error("pk_item_id not found");
     return;
   }

   try {
     setCatalogueImageBusyId(pkItemId);

     // 1. IMAGE -> BASE64
     const imageData = await fileToDataUrl(file);

     const itemName =
       String(row?.item_name || row?.itemName || "").trim() ||
       `catalogue_${pkItemId}`;

     // 2. UPLOAD IMAGE
     const uploadResult = await uploadAdminItemImage({
       userid: userID,
       item_name: itemName,
       image_data: imageData,
       type: "IMG",
     });

     console.log("IMAGE UPLOAD RESPONSE:", uploadResult);

     if (!uploadResult?.success) {
       toast.error(uploadResult?.message || "Image upload failed");
       return;
     }

     const uploadedPhoto = String(uploadResult?.data?.image_url || "").trim();

     console.log("UPLOADED PHOTO URL:", uploadedPhoto);

     if (!uploadedPhoto) {
       toast.error("Uploaded image URL not found");
       return;
     }

     // 3. UPDATE DATABASE IMAGE
     const updateResult = await updateCatalogueItemImage({
       pk_item_id: pkItemId,
       photo: uploadedPhoto,
     });

     console.log("UPDATE IMAGE RESULT:", updateResult);

     if (!updateResult?.success) {
       toast.error(updateResult?.message || "Catalogue image update failed");
       return;
     }

     // 4. IMPORTANT:
     // Local fake update ke bajay backend se fresh data lao
     const refreshedResult = await getCatalogueItemImages({
       fk_user_id: userID,
     });

     console.log("AFTER UPDATE GET RESPONSE:", refreshedResult);

     if (refreshedResult?.success) {
       setCatalogueImageRows(
         Array.isArray(refreshedResult?.data) ? refreshedResult.data : [],
       );
     }

     toast.success("Catalogue image updated successfully");
   } catch (error) {
     console.error("handleCatalogueImageChange error:", error);

     toast.error("Failed to update catalogue image");
   } finally {
     setCatalogueImageBusyId("");
   }
 };

  const openCustomization = (category) => {
    const catId = String(category.cat_id);

    setSelectedCategoryId(catId);

    let parsed = [];

    try {
      parsed = JSON.parse(category.customization || "[]");
    } catch {}

    setPreSelectedCustomization(parsed);
    setSelectedCustomizations(parsed.map((p) => p.customize_field));
    setCustomizationModal(true);
  };

  const openViewItem = (item, category) => {
    markAnchor(category.cat_id, item.item_id);

    let categoryIdFromItem = "";

    try {
      categoryIdFromItem = (JSON.parse(item.category || "[]") || [])[0] || "";
    } catch {
      categoryIdFromItem = item.category || "";
    }

    const prices = Array.isArray(item.prices) ? item.prices : [];
    const first = prices[0] || {};

    const itemWithParent = {
      ...item,
      parentCategoryId: category.cat_id,
      parentCategoryName: category.category_name,
    };

    setViewModalItem(itemWithParent);

    setEditableDetails({
      ...itemWithParent,

      item_id: item.item_id,
      item_name: item.item_name,

      item_description: item.item_description || item.description || "",

      category: item.sub_category || categoryIdFromItem,
      categoryId: String(category.cat_id || ""),

      photo: item.photo || "",
      imageUrl: item.photo || "",

      selectedPriceIndex: 0,

      sizeId: first.size_id || "",
      size: first.size || "",

      sellingPrice: first.price || "",
      homeDeliveryPrice: first.home_delivery_price ?? "",
      takewayPrice: first.takeaway_price ?? "",
      diningPrice: first.dining_price ?? "",
      roomOrderPrice: first.room_order_price ?? "",
      carOrderPrice: first.car_order_price ?? "",

      taxPercentage: first.item_tax ?? "",

      rackNo: item.rackNumber || "",
      expiryDate: item.expiryDate || "",

      variantId: String(first.variantId || first.size_id || ""),
      variantName: first.variantName || first.size || "",

      id: first.id || "",
    });

    setSelectedPrinter(null);
    setViewTab("details");
    setIsEditing(false);
  };

  const openAddVariant = (item) => {
    setSelectedItemForVariant(item);

    setVariantForm({
      variation: "",
      price: "",
    });

    setIsVariantModalOpen(true);
  };

  // ===== Category expand/collapse =====
  const toggleCategory = (catId) => {
    setExpandedCategories((prev) =>
      prev.includes(catId)
        ? prev.filter((id) => id !== catId)
        : [...prev, catId],
    );
  };

  // ===== Effects =====
  useEffect(() => {
    if (!userID) return;
    pullCatalogue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  useEffect(() => {
    allcatalogueAPI();
    fetchVariants();
    getPackagingChargesAndTriggersApi();
  }, [userID]);

  useEffect(() => {
    if (catalogueTab === "rawInventory") {
      getRawInventory();
    }
  }, [catalogueTab]);
  useEffect(() => {
    setCatalogueImageOpen(false);
    setCatalogueImageRows([]);
    setCatalogueImageBusyId("");
  }, [userID]);

  // ===== UI =====
  return (
    <div className="p-4">
      <ToastContainer />
      {/* Header w/ Icon tile */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <BookOpenCheck size={20} />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">Catalogue</div>
            <div className="text-xs text-gray-500">
              Manage items, pricing, availability & printing.
            </div>
          </div>
        </div>

        {/* Client selector */}

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Client</span>
          <ClientSearchSelect
            clients={AllClients}
            value={userID}
            onChange={(client) => {
              setSelectedClient(
                client
                  ? {
                      code: client.us_code,
                      name: client.us_name,
                      pkid: client.id,
                    }
                  : null,
              );
            }}
            placeholder="Select client..."
          />
        </div>
      </div>

      {/* Tabs */}

      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border bg-white p-1">
          {MAIN_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                mainTab === key
                  ? "bg-[#01C37D] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setMainTab(key)}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {mainTab === "table" ? (
        <CatalogueTableView userID={userPKID} />
      ) : mainTab === "UploadItems" ? (
        <AdminUploadItems
          userID={userID}
          onUploaded={() => {
            pullCatalogue();
            allcatalogueAPI(true);
          }}
        />
      ) : (
        <CatalogueView
          catalogueTab={catalogueTab}
          setCatalogueTab={setCatalogueTab}
          searchText={searchText}
          setSearchText={setSearchText}
          userID={userID}
          handleOpenCatalogueImage={handleOpenCatalogueImage}
          handleUpdateSizeId={handleUpdateSizeId}
          setMasterCategoryOpen={setMasterCategoryOpen}
          setAddPrinterOpen={setAddPrinterOpen}
          loading={loading}
          filteredCategories={filteredCategories}
          expandedCategories={expandedCategories}
          toggleCategory={toggleCategory}
          rawItemName={rawItemName}
          setRawItemName={setRawItemName}
          addingRawItem={addingRawItem}
          handleAddRawInventory={handleAddRawInventory}
          loadingRaw={loadingRaw}
          rawInventoryList={rawInventoryList}
          isItemPackagingEnabled={isItemPackagingEnabled}
          packagingBusyId={packagingBusyId}
          togglePackagingCharge={togglePackagingCharge}
          markAnchor={markAnchor}
          openCustomization={openCustomization}
          openViewItem={openViewItem}
          openAddVariant={openAddVariant}
          variantStatusMap={variantStatusMap}
          handleVariantStatusToggle={handleVariantStatusToggle}
          itemVisibilityMap={itemVisibilityMap}
          handleHideItem={handleHideItem}
        />
      )}

      {/* -------- Modals ---------- */}

      {/* Catalogue Image Modal */}
      {catalogueImageOpen && (
        <Modal
          wide
          title="Catalogue Image"
          subtitle={`Client: ${userID}`}
          onClose={() => {
            if (!catalogueImageBusyId) {
              setCatalogueImageOpen(false);
            }
          }}
          headerRight={
            <button
              type="button"
              onClick={loadCatalogueImageRows}
              disabled={catalogueImageLoading || Boolean(catalogueImageBusyId)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {catalogueImageLoading ? "Loading..." : "Refresh"}
            </button>
          }
        >
          {catalogueImageLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#01C37D]" />

                <p className="mt-3 text-sm text-gray-500">
                  Loading catalogue items...
                </p>
              </div>
            </div>
          ) : catalogueImageRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">
                No catalogue items found
              </p>

              <p className="mt-1 text-xs text-gray-500">Client: {userID}</p>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="relative w-full max-w-sm">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    type="text"
                    value={catalogueImageSearch}
                    onChange={(e) => setCatalogueImageSearch(e.target.value)}
                    placeholder="Search item..."
                    className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-8 text-sm outline-none transition focus:border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/20"
                  />

                  {catalogueImageSearch && (
                    <button
                      type="button"
                      onClick={() => setCatalogueImageSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div className="shrink-0 text-xs text-gray-500">
                  {filteredCatalogueImageRows.length} items
                </div>
              </div>

              {filteredCatalogueImageRows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 py-10 text-center">
                  <p className="text-sm font-medium text-gray-700">
                    No matching item found
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Search: {catalogueImageSearch}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {filteredCatalogueImageRows.map((row, index) => {
                    const pkItemId = String(
                      row?.pk_item_id ?? row?.pkItemId ?? row?.item_id ?? "",
                    ).trim();

                    const itemName =
                      row?.item_name || row?.itemName || `Item ${index + 1}`;

                    const photo =
                      row?.photo ||
                      row?.image ||
                      row?.image_url ||
                      row?.imageUrl ||
                      "";

                    const isUpdating = catalogueImageBusyId === pkItemId;

                    return (
                      <div
                        key={pkItemId || `${itemName}-${index}`}
                        className="relative rounded-xl border border-gray-200 bg-white p-2 shadow-sm transition hover:shadow-md"
                      >
                        {/* EDIT IMAGE ICON */}
                        <label
                          title={
                            isUpdating ? "Updating image..." : "Update Image"
                          }
                          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full shadow-sm transition ${
                            isUpdating || !pkItemId
                              ? "cursor-not-allowed bg-gray-200 text-gray-400"
                              : "cursor-pointer bg-white text-[#01C37D] hover:bg-[#01C37D] hover:text-white"
                          }`}
                        >
                          {isUpdating ? (
                            <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-[#01C37D]" />
                          ) : (
                            <Pencil size={12} strokeWidth={2.4} />
                          )}

                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={isUpdating || !pkItemId}
                            onChange={(event) =>
                              handleCatalogueImageChange(event, row)
                            }
                          />
                        </label>

                        {/* IMAGE */}
                        <div className="flex items-center justify-center py-1">
                          {photo ? (
                            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50 p-1">
                              <img
                                src={photo}
                                alt={itemName}
                                className="h-full w-full rounded-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-gray-200 bg-gray-100 px-2 text-center text-[10px] text-gray-400">
                              No image
                            </div>
                          )}
                        </div>

                        {/* ITEM DETAILS */}
                        <div className="mt-1 text-center">
                          <div
                            className="truncate text-xs font-semibold text-gray-900"
                            title={itemName}
                          >
                            {itemName}
                          </div>

                          {/* <div className="mt-0.5 truncate text-[10px] text-gray-500">
                            ID:{" "}
                            <span className="font-medium text-gray-700">
                              {pkItemId || "-"}
                            </span>
                          </div> */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal>
      )}

      {/* Customization Modal */}
      {customizationModal && (
        <Modal
          onClose={() => setCustomizationModal(false)}
          title="Customize Category"
        >
          <p className="mb-2 text-sm text-gray-600">
            Select customization fields to apply to this category.
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {customizationSets.map((set) => {
              const checked = selectedCustomizations.includes(
                set.customize_field,
              );
              return (
                <label
                  key={set.customize_field}
                  className={`cursor-pointer rounded-lg border p-3 ${checked ? "border-[#01C37D] bg-[#01C37D]/5" : ""}`}
                  onClick={() =>
                    setSelectedCustomizations((prev) =>
                      prev.includes(set.customize_field)
                        ? prev.filter((f) => f !== set.customize_field)
                        : [...prev, set.customize_field],
                    )
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="font-semibold">{set.customize_field}</div>
                    <input type="checkbox" checked={checked} readOnly />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {set.options.map((opt) => (
                      <span
                        key={opt}
                        className="rounded-full border px-2 py-0.5 text-xs text-gray-600"
                      >
                        {opt}
                      </span>
                    ))}
                  </div>
                </label>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              className="rounded-lg border px-3 py-1.5 text-sm"
              onClick={() => setCustomizationModal(false)}
            >
              Close
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-lg bg-[#01C37D] px-3 py-1.5 text-sm font-semibold text-white"
              onClick={applyCustomizationToCategory}
            >
              <SlidersHorizontal size={14} /> Apply
            </button>
          </div>
        </Modal>
      )}

      <CatalogueItemModal
        viewModalItem={viewModalItem}
        setViewModalItem={setViewModalItem}
        viewTab={viewTab}
        setViewTab={setViewTab}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        editableDetails={editableDetails}
        setEditableDetails={setEditableDetails}
        handleEditClick={handleEditClick}
        handleSaveFromViewMode={handleSaveFromViewMode}
        subCategories={subCategories}
        racks={racks}
        variants={variants}
        availabilityData={availabilityData}
        setAvailabilityData={setAvailabilityData}
        submitAvailability={submitAvailability}
        fetchPrinters={fetchPrinters}
        printerLoading={printerLoading}
        printers={printers}
        setSelectedPrinter={setSelectedPrinter}
        savePrinterToItem={savePrinterToItem}
        setAddPrinterOpen={setAddPrinterOpen}
      />

      {/* Add Variant Modal */}
      {isVariantModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">
                Add Variant · {selectedItemForVariant?.item_name}
              </h2>
              <button onClick={() => setIsVariantModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <div className="space-y-3">
              {/* Variant Name */}
              <div>
                <label className="text-sm text-gray-600">
                  Variation Name / Label
                </label>
                <input
                  type="text"
                  placeholder="Eg. Small, Medium, Large"
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm outline-none"
                  value={variantForm.variation}
                  onChange={(e) =>
                    setVariantForm({
                      ...variantForm,
                      variation: e.target.value,
                    })
                  }
                />
              </div>

              {/* Price */}
              <div>
                <label className="text-sm text-gray-600">Price</label>
                <input
                  type="number"
                  placeholder="Eg. 199"
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm outline-none"
                  value={variantForm.price}
                  onChange={(e) =>
                    setVariantForm({ ...variantForm, price: e.target.value })
                  }
                />
              </div>

              {/* Note */}
              <p className="text-xs text-gray-400">
                <span className="text-pink-500 font-medium">Note:</span>{" "}
                variantId will be empty; backend will treat this as a new price
                variation for this item.
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 border rounded-lg text-sm"
                onClick={() => setIsVariantModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#01C37D] text-white rounded-lg text-sm"
                onClick={handleAddVariant}
              >
                Save Variant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            const fn = confirm.onConfirm;
            setConfirm(null);
            fn?.();
          }}
        />
      )}

      {addPrinterOpen && (
        <AddPrinterModal
          open={addPrinterOpen}
          onClose={() => setAddPrinterOpen(false)}
          api={api}
          userID={userID}
          onSaved={() => {
            fetchPrinters();
          }}
        />
      )}

      {/* ✅ Master Category Modal */}
      <AddMasterCategoryModal
        open={masterCategoryOpen}
        onClose={() => setMasterCategoryOpen(false)}
        api={api}
      />
    </div>
  );
}

/* ------------- tiny UI atoms (local) ------------- */

function Modal({ title, subtitle, children, onClose, headerRight, wide }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3">
      <div
        className={`max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white p-5 shadow-xl ${
          wide ? "max-w-5xl" : "max-w-3xl"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-xl font-bold leading-tight text-gray-900">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {headerRight}
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-100" />
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function ConfirmDialog({ title, message, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-3">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
        <div className="mb-2 text-base font-bold">{title}</div>
        <div className="text-sm text-gray-600">{message}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            className="rounded-lg border px-3 py-1.5 text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-[#01C37D] px-3 py-1.5 text-sm font-semibold text-white"
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
